import { createEffect, sample } from 'effector'
import * as wasmFeatureDetect from 'wasm-feature-detect'

import type { PortalProfile } from '@portal/portal-api'

import { flyerAPI } from '@/shared/api'
import { env } from '@/shared/config'
import { analytics } from '@/shared/lib/analytics'
import { parseUserAgent } from '@/shared/lib/user-agent'

const sendPortalVisitEventFx = createEffect<PortalProfile, void>()

sample({
    clock: analytics.login,
    target: sendPortalVisitEventFx,
})

sendPortalVisitEventFx.use(async (profile) => {
    const userAgent = parseUserAgent()

    await flyerAPI.sendEvent({
        event_name: 'portal_visit',
        event_data: {
            url: window.location.href,
            referrer: document.referrer,
            game_platform: 'portal',
            portal_version: env.BUILD_VERSION,
            context: {
                probe: {
                    // rust_module: await probeRustModule(),
                    // wasm_support: await probeWasmFeatures(),
                    wasm_available: typeof WebAssembly !== 'undefined',
                    cookie_enabled: navigator.cookieEnabled,
                    // local_storage: TODO
                    // session_storage: TODO
                },
                user: {
                    support_id: profile.supportId,
                    public_id: profile.publicId,
                    is_new: profile.isNewAccount,
                },
                browser: {
                    name: userAgent.browser.name,
                    version: userAgent.browser.version,
                },
                cpu: {
                    architecture: userAgent.cpu.architecture,
                    concurrency: navigator.hardwareConcurrency,
                },
                referrer: document.referrer,
                device: {
                    model: userAgent.device.model,
                    type: userAgent.device.type,
                    vendor: userAgent.device.vendor,
                },
                os: {
                    name: userAgent.os.name,
                    version: userAgent.os.version,
                },
                engine: {
                    name: userAgent.engine.name,
                    version: userAgent.engine.version,
                },
                screen: {
                    width: window.screen.width,
                    height: window.screen.height,
                    pixel_ratio: window.devicePixelRatio,
                },
            },
        },
    })
})

async function _probeWasmFeatures(): Promise<{ [key in keyof typeof wasmFeatureDetect]: boolean }> {
    return {
        bigInt: await wasmFeatureDetect.bigInt(),
        bulkMemory: await wasmFeatureDetect.bulkMemory(),
        exceptions: await wasmFeatureDetect.exceptions(),
        extendedConst: await wasmFeatureDetect.extendedConst(),
        gc: await wasmFeatureDetect.gc(),
        jspi: await wasmFeatureDetect.jspi(),
        memory64: await wasmFeatureDetect.memory64(),
        multiMemory: await wasmFeatureDetect.multiMemory(),
        multiValue: await wasmFeatureDetect.multiValue(),
        mutableGlobals: await wasmFeatureDetect.mutableGlobals(),
        referenceTypes: await wasmFeatureDetect.referenceTypes(),
        relaxedSimd: await wasmFeatureDetect.relaxedSimd(),
        saturatedFloatToInt: await wasmFeatureDetect.saturatedFloatToInt(),
        signExtensions: await wasmFeatureDetect.signExtensions(),
        simd: await wasmFeatureDetect.simd(),
        streamingCompilation: await wasmFeatureDetect.streamingCompilation(),
        tailCall: await wasmFeatureDetect.tailCall(),
        threads: await wasmFeatureDetect.threads(),
        typeReflection: await wasmFeatureDetect.typeReflection(),
    }
}
